import React, { lazy, Suspense, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { ThemeProvider } from 'react-jss';
import { ReactNotifications } from 'react-notifications-component';
import { useFullscreen } from 'react-use';
import { TourProvider } from '@reactour/tour';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/it';
import 'dayjs/locale/de';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/pl';
import 'dayjs/locale/pt';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ToastContainer, toast } from 'react-toastify';
import i18n from 'i18next';
import ThemeContext from '../contexts/themeContext';
import AuthContext from '../contexts/authContext';
import { useOrganization } from '../contexts/organizationContext';
import Wrapper from '../layout/Wrapper/Wrapper';
import Portal from '../layout/Portal/Portal';
import useDarkMode from '../hooks/useDarkMode';
import COLORS from '../common/data/enumColors';
import { getOS } from '../helpers/helpers';
import steps, { styles } from '../steps';
import AsideRoutes from '../layout/Aside/AsideRoutes';
import { ToastCloseButton } from '../components/bootstrap/Toasts';
import { UserType } from '../common/data/dummyRolesData';
import authService from '../services/api/authService';
import Loading from '../components/Loading';
import { FeatureErrorModal } from '../components/features';

/**
 * Onboarding wizards (full-page, no sidebar/header).
 * @userstory US-ONBOARD-CO-01, US-ONBOARD-TA-01
 * @task TASK-OBC-05, TASK-OBT-05
 */
const CompanyOnboardingWizard = lazy(
	() => import('../pages/company/onboarding/CompanyOnboardingWizard'),
);
const TalentOnboardingWizard = lazy(
	() => import('../pages/talent/onboarding/TalentOnboardingWizard'),
);

const App = () => {
	getOS();

	dayjs.extend(localizedFormat);
	dayjs.extend(relativeTime);

	// Sync dayjs global locale with current i18n language (affects fromNow(), format(), etc.)
	useEffect(() => {
		const syncDayjsLocale = (lng: string) => {
			const shortLang = lng.split('-')[0];
			dayjs.locale(shortLang);
		};
		syncDayjsLocale(i18n.language);
		i18n.on('languageChanged', syncDayjsLocale);
		return () => {
			i18n.off('languageChanged', syncDayjsLocale);
		};
	}, []);

	// Get navigation and location functions
	const navigate = useNavigate();
	const location = useLocation();

	// Access AuthContext
	const { isAuthenticated, isInitialized, isBootstrapped, login, syncUserData, userData } =
		useContext(AuthContext);

	// Access OrganizationContext
	const {
		organizations,
		isLoading: isOrgLoading,
		isInitialized: isOrgInitialized,
	} = useOrganization();

	// Variable to track if login is in progress to prevent multiple login attempts
	const loginAttemptRef = useRef(false);

	// Handle API timeout errors
	useEffect(() => {
		const handleApiTimeout = (event: CustomEvent) => {
			// Show a user-friendly error message
			toast.error(
				`API request timed out. Please check your internet connection or try again later.`,
				{
					autoClose: 5000,
					closeButton: true,
					position: 'top-right',
				},
			);

			console.error('API timeout:', event.detail);
		};

		// Add event listener
		window.addEventListener('api-timeout-error', handleApiTimeout as EventListener);

		// Clean up
		return () => {
			window.removeEventListener('api-timeout-error', handleApiTimeout as EventListener);
		};
	}, []);

	// Handle role-based redirection after successful authentication
	// @userstory US-ONBOARD-CO-05, US-ONBOARD-TA-01
	// @task TASK-OBC-05, TASK-OBT-05
	useEffect(() => {
		console.log('[App/App.tsx] Redirect effect fired', {
			isAuthenticated,
			isBootstrapped,
			userRole: userData.role,
			pathname: location.pathname,
			isOrgInitialized,
			isOrgLoading,
			organizationsCount: organizations?.length || 0,
			dateCompletionOnboarding: userData.dateCompletionOnboarding,
		});

		// Wait for bootstrap to complete before making onboarding decisions,
		// because dateCompletionOnboarding is loaded via syncUserData during bootstrap.
		if (
			isAuthenticated &&
			isBootstrapped &&
			userData.role &&
			(location.pathname === '/' ||
				location.pathname === '/login' ||
				location.pathname === '/auth/login' ||
				location.pathname === '/auth-pages/login' ||
				location.pathname === '/company/onboarding' ||
				location.pathname === '/talent/onboarding')
		) {
			// If authenticated and on login/home page, redirect to appropriate dashboard based on role
			if (userData.role === UserType.COMPANY) {
				// Wait for organization context to be initialized before making routing decisions
				// isInitialized becomes true only after the initial fetch completes (success or error)
				if (isOrgInitialized && !isOrgLoading) {
					// Onboarding gate: show wizard ONLY if user has not completed onboarding AND has no organizations
					// US-ONBOARD-CO-01: Wizard shown when dateCompletionOnboarding is null AND user has no organizations
					// US-ONBOARD-CO-06: Users who are auto-accepted into an organization via invitation bypass the wizard
					if (!userData.dateCompletionOnboarding && organizations.length === 0) {
						console.log(
							'[App/App.tsx] Company user has not completed onboarding and has no organizations, redirecting to /company/onboarding',
						);
						navigate('/company/onboarding');
						return;
					}
					// User has completed onboarding OR has organizations (e.g., accepted invitation)

					if (organizations && organizations.length > 0) {
						console.log('[App/App.tsx] Redirecting company user to /company/dashboard');
						navigate('/company/dashboard');
					} else {
						console.log(
							'[App/App.tsx] Redirecting company user to /company/organizations (no orgs)',
						);
						navigate('/company/organizations');
					}
				} else {
					console.log('[App/App.tsx] Waiting for organization context to initialize...');
				}
				// If not initialized yet, don't redirect - wait for the fetch to complete
			} else if (userData.role === UserType.FREELANCE) {
				// Onboarding gate: new talent users must complete the onboarding wizard first
				if (!userData.dateCompletionOnboarding) {
					console.log(
						'[App/App.tsx] Talent user has not completed onboarding, redirecting to /talent/onboarding',
					);
					navigate('/talent/onboarding');
					return;
				}
				// Alliance US-10 / Flow step 7-8: Check if user arrived via invitation link.
				// InvitationTokenCapture (ContentRoutes) saves the token to localStorage
				// before MSAL auth. After auth, redirect to pending invitations page.
				const ALLIANCE_INVITATION_TOKEN_KEY = 'ot_alliance_invitation_token';
				const pendingInvitationToken = localStorage.getItem(ALLIANCE_INVITATION_TOKEN_KEY);
				if (pendingInvitationToken) {
					localStorage.removeItem(ALLIANCE_INVITATION_TOKEN_KEY);
					console.log(
						'[App/App.tsx] Alliance invitation token found, redirecting to pending invitations',
					);
					navigate('/talent/alliance/pending');
				} else {
					console.log('[App/App.tsx] Redirecting freelance user to /talent/dashboard');
					navigate('/talent/dashboard');
				}
			}
		}
	}, [
		isAuthenticated,
		isBootstrapped,
		userData.role,
		userData.dateCompletionOnboarding,
		location.pathname,
		navigate,
		organizations,
		isOrgLoading,
		isOrgInitialized,
	]);

	// Handle organization-based redirection for company users
	useEffect(() => {
		if (
			isAuthenticated &&
			isBootstrapped &&
			userData.role === UserType.COMPANY &&
			isOrgInitialized &&
			!isOrgLoading &&
			location.pathname === '/company/dashboard'
		) {
			// Onboarding gate takes priority over org-based redirect
			// BUT only if user has NO organizations (US-ONBOARD-CO-06: users with orgs skip wizard)
			if (
				!userData.dateCompletionOnboarding &&
				(!organizations || organizations.length === 0)
			) {
				console.log(
					'[App/App.tsx] Effect #2: On dashboard, no orgs and no onboarding completion → /company/onboarding',
				);
				navigate('/company/onboarding');
				return;
			}
			// If company user is on dashboard but has no organizations, redirect to organizations page
			if (!organizations || organizations.length === 0) {
				console.log(
					'[App/App.tsx] Effect #2: On dashboard with no orgs → /company/organizations',
				);
				navigate('/company/organizations');
			}
		}
	}, [
		isAuthenticated,
		isBootstrapped,
		userData.role,
		userData.dateCompletionOnboarding,
		organizations,
		isOrgLoading,
		isOrgInitialized,
		location.pathname,
		navigate,
	]);

	/**
	 * Global onboarding gate — catches any navigation to app pages while onboarding
	 * is not yet completed. This covers edge cases such as direct URL entry,
	 * DashboardView redirecting to /company/organizations, or browser back/forward.
	 * @userstory US-ONBOARD-CO-05, US-ONBOARD-TA-01
	 * @task TASK-OBC-05, TASK-OBT-05
	 */
	useEffect(() => {
		if (!isAuthenticated || !isBootstrapped || !userData.role) return;

		const path = location.pathname;

		// Don't redirect if on public/auth routes
		if (
			path === '/' ||
			path === '/login' ||
			path === '/auth/login' ||
			path === '/auth-pages/login' ||
			path.startsWith('/auth/')
		)
			return;

		// Reverse onboarding gate: users who ALREADY completed onboarding
		// must not access the wizard again (it would create duplicate orgs).
		if (
			path === '/company/onboarding' &&
			userData.role === UserType.COMPANY &&
			userData.dateCompletionOnboarding
		) {
			console.log(
				'[App/App.tsx] Onboarding already completed, redirecting away from /company/onboarding → /',
			);
			navigate('/');
			return;
		}
		if (
			path === '/talent/onboarding' &&
			userData.role === UserType.FREELANCE &&
			userData.dateCompletionOnboarding
		) {
			console.log(
				'[App/App.tsx] Onboarding already completed, redirecting away from /talent/onboarding → /',
			);
			navigate('/');
			return;
		}

		// Don't redirect if already on the onboarding page (not yet completed)
		if (path === '/company/onboarding' || path === '/talent/onboarding') return;

		// Company users must complete onboarding before accessing any /company/* route
		// BUT only if they have NO organizations (US-ONBOARD-CO-06: invited users with orgs skip wizard)
		if (
			userData.role === UserType.COMPANY &&
			!userData.dateCompletionOnboarding &&
			path.startsWith('/company')
		) {
			// Check if user has organizations (from accepted invitation)
			if (isOrgInitialized && (!organizations || organizations.length === 0)) {
				console.log(
					'[App/App.tsx] Effect #3: Onboarding gate blocking',
					path,
					'→ /company/onboarding',
				);
				navigate('/company/onboarding');
				return;
			} else if (!isOrgInitialized) {
				// If org context not initialized yet, don't redirect (let Effect #1 handle it)
				console.log(
					'[App/App.tsx] Effect #3: Waiting for org context before applying onboarding gate',
				);
				return;
			}
			// If user has organizations, let them through (they skip onboarding per US-ONBOARD-CO-06)
		}

		// Talent users must complete onboarding before accessing any /talent/* route
		if (
			userData.role === UserType.FREELANCE &&
			!userData.dateCompletionOnboarding &&
			path.startsWith('/talent')
		) {
			console.log(
				'[App/App.tsx] Effect #3: Onboarding gate blocking',
				path,
				'→ /talent/onboarding',
			);
			navigate('/talent/onboarding');
		}
	}, [
		isAuthenticated,
		isBootstrapped,
		userData.role,
		userData.dateCompletionOnboarding,
		location.pathname,
		navigate,
		organizations,
		isOrgInitialized,
	]);

	// Trigger login if not authenticated and MSAL is initialized
	useEffect(() => {
		// Skip if not initialized or already authenticated
		if (!isInitialized || isAuthenticated) {
			// If authenticated, reset login attempt flag
			if (isAuthenticated) {
				loginAttemptRef.current = false;

				// Sync user data only if essential data is missing to prevent redundant API calls
				if (syncUserData && (!userData.id || !userData.email)) {
					syncUserData().catch((error) => {
						console.error('Failed to sync user data:', error);
						// If sync fails due to invalid token, we should logout and login again
						if (error.response?.status === 401) {
							console.log('Auth token invalid, restarting authentication flow...');
							authService.clearAuth();
							// We'll let the next render cycle handle the login
						}
					});
				}
			}
			return;
		}

		// Only proceed if not authenticated and not already attempting login
		if (!loginAttemptRef.current) {
			// Check if access token exists and is valid
			const accessToken = authService.getToken();

			// If no token or token is invalid, trigger login
			if (!accessToken || !authService.isTokenValid(accessToken)) {
				console.log('No valid access token found, initiating Azure AD B2C login flow...');
				// Set flag to prevent multiple login attempts
				loginAttemptRef.current = true;

				// Clear any stale MSAL state first
				Object.keys(localStorage)
					.filter((key) => key.includes('msal.state') || key.includes('msal.interaction'))
					.forEach((key) => localStorage.removeItem(key));

				// Short delay to avoid interaction_in_progress errors
				setTimeout(() => {
					// Trigger the Azure AD B2C login flow - only do this once
					login().catch((error) => {
						console.error('Login error:', error);
						// Don't immediately reset the flag on interaction_in_progress errors
						if (
							error &&
							typeof error === 'object' &&
							'errorCode' in error &&
							error.errorCode === 'interaction_in_progress'
						) {
							// For interaction_in_progress, let the ongoing auth complete
							console.log('Waiting for ongoing authentication to complete...');
						} else {
							// Reset flag for other errors so we can try again
							loginAttemptRef.current = false;
						}
					});
				}, 500);
			}
		}
	}, [
		isInitialized,
		isAuthenticated,
		login,
		syncUserData,
		userData.id,
		userData.email,
		location.pathname,
	]);

	/**
	 * Dark Mode
	 */
	const { themeStatus, darkModeStatus } = useDarkMode();
	const theme = {
		theme: themeStatus,
		primary: COLORS.PRIMARY.code,
		secondary: COLORS.SECONDARY.code,
		success: COLORS.SUCCESS.code,
		info: COLORS.INFO.code,
		warning: COLORS.WARNING.code,
		danger: COLORS.DANGER.code,
		dark: COLORS.DARK.code,
		light: COLORS.LIGHT.code,
	};

	useEffect(() => {
		if (darkModeStatus) {
			document.documentElement.setAttribute('theme', 'dark');
			document.documentElement.setAttribute('data-bs-theme', 'dark');
		}
		return () => {
			document.documentElement.removeAttribute('theme');
			document.documentElement.removeAttribute('data-bs-theme');
		};
	}, [darkModeStatus]);

	/**
	 * Full Screen
	 */
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const ref = useRef(null);
	useFullscreen(ref, fullScreenStatus, {
		onClose: () => setFullScreenStatus(false),
	});

	/**
	 * Modern Design
	 */
	useLayoutEffect(() => {
		if (process.env.REACT_APP_MODERN_DESGIN === 'true') {
			document.body.classList.add('modern-design');
		} else {
			document.body.classList.remove('modern-design');
		}
	});

	return (
		<ThemeProvider theme={theme}>
			<TourProvider steps={steps} styles={styles} showNavigation={false} showBadge={false}>
				<div
					ref={ref}
					className='app'
					style={{
						backgroundColor: fullScreenStatus ? 'var(--bs-body-bg)' : undefined,
						zIndex: fullScreenStatus ? 1 : undefined,
						overflow: fullScreenStatus ? 'scroll' : undefined,
					}}>
					{isInitialized && isAuthenticated && isBootstrapped ? (
						location.pathname === '/company/onboarding' ||
						location.pathname === '/talent/onboarding' ? (
							<Suspense fallback={<Loading />}>
								{location.pathname === '/company/onboarding' ? (
									<CompanyOnboardingWizard />
								) : (
									<TalentOnboardingWizard />
								)}
							</Suspense>
						) : (
							<div className='app-main-with-panel'>
								<AsideRoutes />
								<Wrapper />
							</div>
						)
					) : (
						<Loading />
					)}
				</div>
				<Portal id='portal-notification'>
					<ReactNotifications />
				</Portal>
				<ToastContainer
					closeButton={ToastCloseButton}
					toastClassName='toast'
					theme={darkModeStatus ? 'dark' : 'light'}
				/>
				<FeatureErrorModal />
			</TourProvider>
		</ThemeProvider>
	);
};

export default App;



